import React from "react";
import {
  Button,
  FormGroup,
  FormControlLabel,
  Switch,
  Box,
  Typography,
} from "@mui/material";

import { useTranslation } from "@/lib/i18n";
import { NavigationElement } from "@/lib/api";
import {
  If,
  Modal,
  ModalActions,
  ModalComponent,
  ModalContent,
  ModalContentText,
  ModalTitle,
} from "@/features/shared/components";
import { useDeleteNavigationElementMutation } from "@/features/admin/features/pages-management";
import { useAdminDashboard } from "@/features/admin/hooks";
import { useDashboardNavigationElements } from "@/features/admin/hooks/queries";

type Props = {
  triggerElement: React.ReactElement;
  onClose?: () => void;
  navigationElement: NavigationElement;
};

export default function RemoveFolderConfirm({
  navigationElement,
  onClose,
  triggerElement,
}: Props) {
  const { t } = useTranslation();
  const dashboard = useAdminDashboard();
  const navigationElements = useDashboardNavigationElements(dashboard.id).data ?? [];
  const deleteNavigationElementMutation = useDeleteNavigationElementMutation();

  const subPages = React.useMemo(
    () =>
      navigationElements?.filter((element) => element.parent_id === navigationElement.id),
    [navigationElements, navigationElement],
  );

  const hasPages = subPages.length > 0;

  const [removePagesCheck, setRemovePagesCheck] = React.useState(false);

  const modalRef = React.useRef<ModalComponent>(null);

  async function handleConfirm() {
    deleteNavigationElementMutation.mutate(navigationElement.id, {
      onSuccess: () => {
        modalRef.current?.close();
      },
    });
  }

  return (
    <Modal
      ref={modalRef}
      triggerElement={triggerElement}
      onClose={() => onClose?.()}
      aria-labelledby="remove-folder-dialog-title"
      aria-describedby="remove-folder-dialog-description"
    >
      <ModalTitle id="remove-folder-dialog-title">
        {t("admin.pages.remove_folder_confirm.title")}
      </ModalTitle>
      <ModalContent>
        <ModalContentText id="remove-folder-dialog-description">
          {t("admin.pages.remove_folder_confirm.body")}

          <If condition={hasPages}>
            <Box sx={{ my: 3 }}>
              <Typography fontWeight="bold">
                {t("admin.pages.remove_folder_confirm.pages_that_will_be_removed")}
              </Typography>
              <ul style={{ paddingLeft: 15 }}>
                {subPages.map((subPage) => (
                  <li key={subPage.id}>{subPage.title}</li>
                ))}
              </ul>
            </Box>

            <FormGroup>
              <FormControlLabel
                control={<Switch />}
                onChange={(_, checked) => setRemovePagesCheck(checked)}
                label={t("admin.pages.remove_folder_confirm.pages_remove_checkbox")}
              />
            </FormGroup>
          </If>
        </ModalContentText>
      </ModalContent>
      <ModalActions>
        <Button onClick={() => modalRef.current?.close()}>
          {t("general.confirm.cancel")}
        </Button>
        <Button disabled={hasPages && !removePagesCheck} onClick={handleConfirm} autoFocus>
          {t("general.confirm.ok")}
        </Button>
      </ModalActions>
    </Modal>
  );
}
